import { useCallback, useMemo } from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { getWeekStart } from '../../utils/date.ts';
import { Label } from '@progress/kendo-react-labels';
import { TextBox } from '@progress/kendo-react-inputs';
import { LayoutDrawer } from '../../components/layout/layout-drawer.tsx';
import { ListView } from '../../components/list/list-view.tsx';
import { ListViewItem } from '../../components/list/list-view-item.tsx';
import { ListViewItemText } from '../../components/list/list-view-item-text.tsx';
import { ListViewItemSeparator } from '../../components/list/list-view-item-separator.tsx';
import type { ListViewRenderProps } from '../../components/list/list-view.types.ts';
import type { MetricsSummaryDrawerProps, MetricsSummaryListItem } from './metrics-summary-drawer.types.ts';
import { SamplingJobChartType } from '../chart/sampling-metrics-chart.types.ts';
import styles from './metrics-summary-drawer.module.scss';

export function MetricsSummaryDrawer({
  data,
  width,
  maxWidth,
  position,
  chartType,
  samplingCapacity,
  open,
  onOpen,
}: MetricsSummaryDrawerProps) {
  const { totalSampleCount, avgWeeklySamples, metricCounts } = useMemo(() => {
    if (data.length === 0) {
      return { totalSampleCount: 0, avgWeeklySamples: '0', metricCounts: [] };
    }

    const uniqueWeeks = new Set<string>();

    const totalSamples = data.reduce((acc, item) => {
      const weekStart = getWeekStart(new Date(item.plannedDate));
      uniqueWeeks.add(weekStart.toISOString());
      return acc + (item.sampleCount || 0);
    }, 0);

    const weeklyAvg = totalSamples / (uniqueWeeks.size || 1);

    const metricCounts = data.reduce<Record<string, number>>((acc, item) => {
      let key: string | undefined;

      switch (chartType) {
        case SamplingJobChartType.Sampler:
          key = item.sampler;
          break;
        case SamplingJobChartType.JobStatus:
          key = item.jobStatus;
          break;
        case SamplingJobChartType.SampleType:
          key = item.sampleType;
          break;
        case SamplingJobChartType.SamplingStatus:
          key = Boolean(item.sampled).toString();
          break;
      }

      if (key !== undefined && key !== null && key !== '') {
        acc[key] = (acc[key] || 0) + (item.sampleCount || 0);
      }

      return acc;
    }, {});

    const metricCountsList: MetricsSummaryListItem[] = Object.entries(metricCounts)
      .map(([key, count]) => {
        let title = key;
        if (chartType === SamplingJobChartType.SamplingStatus) {
          title = key === 'true' ? 'Samples Taken' : 'Samples Planned';
        }

        return { title, count };
      })
      .sort((a, b) => b.count - a.count);

    return {
      totalSampleCount: totalSamples,
      avgWeeklySamples: formatNumber(weeklyAvg, '0'),
      metricCounts: metricCountsList,
    };
  }, [chartType, data]);

  const renderItem = useCallback(
    ({ dataItem }: ListViewRenderProps<MetricsSummaryListItem>) => (
      <ListViewItem>
        <ListViewItemText flex={2}>
          <b>{dataItem.title}</b>
        </ListViewItemText>
        <ListViewItemSeparator />
        <ListViewItemText flex={1}>{dataItem.count}</ListViewItemText>
      </ListViewItem>
    ),
    []
  );

  return (
    <LayoutDrawer
      title={'Metrics Summary'}
      width={width}
      maxWidth={maxWidth}
      position={position}
      open={open}
      onOpen={onOpen}
    >
      <div className={styles.content}>
        {!data || data.length === 0 ? (
          <div className={styles.noContent}>No sampling metrics available.</div>
        ) : (
          <>
            <div className={styles.card}>
              <div className={styles.cardHeader}>Overview</div>
              <div className={styles.cardBody}>
                <div className={styles.formGrid}>
                  <Label>Sampling Capacity:</Label>
                  <TextBox readOnly value={samplingCapacity} />
                  <Label>Total Sample Count:</Label>
                  <TextBox readOnly value={totalSampleCount} />
                  <Label>Avg. Weekly Samples:</Label>
                  <TextBox readOnly value={avgWeeklySamples} />
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardHeader}>Breakdown</div>
              <div className={styles.cardBody}>
                <ListView data={metricCounts} item={renderItem} />
              </div>
            </div>
          </>
        )}
      </div>
    </LayoutDrawer>
  );
}
